import {
  BodyText,
  Box,
  Button,
  CircularProgress,
  Icon,
  SmallText,
  Tooltip,
  useTheme
} from '@epilot/base-elements'
import { File } from '@epilot/customer-portal-client'
import { Download } from '@epilot360/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { ChangeEvent, useContext, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { userActivityEvents } from 'analytics/utils'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import { DocumentIcon } from 'components/icons/icons'
import { toast } from 'components/ui/toaster'
import { useAnalytics } from 'context/AnalyticsContext'
import useDownloadZip from 'hooks/common/useDownloadZip'
import { useGetDocuments } from 'hooks/documents/useGetDocuments'
import useDatadog from 'hooks/useDataDog'
import { getIsFileExtensionAllowed } from 'utils/FileUtils'
import { isConfigMode, isPreviewMode, MEDIA_SIZES } from 'utils/StyleUtils'

import { EntityItem, FileItem } from '../../../api/ApiTypes'
import {
  getCustomerApiClient,
  type Components
} from '../../../api/CustomerApiClient'
import { getFilesClient } from '../../../api/FilesClient'
import PortalConfigContext from '../../../context/PortalConfigContext'
import useEcpTranslation from '../../../hooks/common/useEcpTranslation'
import { useDocumentPermissions } from '../../../hooks/documents/useDocumentPermissions'
import useFileAccess from '../../../hooks/documents/useFileAccess'
import { EntityType } from '../../../utils/EntityUtils'
import AlertMessage from '../AlertMessage'
import { GreyStaticSkeleton } from '../entity/EntityUI'

const removeFileExtension = (fileName: string): string => {
  if (fileName.includes('.')) {
    return fileName.substring(0, fileName.lastIndexOf('.'))
  }

  return fileName
}

interface FilePayload {
  filename: string
  s3ref: {
    bucket: string
    key: string
  }
}

type DeleteFilePayload = Components.Schemas.DeleteEntityFile

interface SaveFilePayload {
  entity_id: string
  entity_type: string
  files: FilePayload[]
}

interface Props {
  files: EntityItem[]
  entityId: string
  entityType: EntityType
}

const MAX_FILE_SIZE = 20

const PAGE_SIZE = 20

const RequestDocuments = ({ entityId, entityType }: Props) => {
  const { t } = useEcpTranslation()
  const queryClient = useQueryClient()
  const { data: portalConfigData } = useContext(PortalConfigContext)
  const { checkFileLink } = useFileAccess()
  const { downloadAsZip } = useDownloadZip()
  const { isDeleteDocumentDetailsPermitted, isUploadDocumentDetailsPermitted } =
    useDocumentPermissions()
  const datadogRum = useDatadog()
  const theme = useTheme()
  const { analytics } = useAnalytics()

  const { data, fetchMore, isLoadingMore, status } = useGetDocuments({
    entityId,
    size: PAGE_SIZE
  })

  const hasMore = data.hasMore
  const files = data?.results || []

  const FilesClient = useMemo(
    () => getFilesClient(portalConfigData.portalConfig.organization_id),
    [portalConfigData.portalConfig.organization_id]
  )

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDownloadingAll, setIsDownloadingAll] = useState(false)
  const [isDeletingId, setIsDeletingId] = useState<string | undefined>(
    undefined
  )
  const isDeleting = Boolean(isDeletingId)

  const saveFileMutation = useMutation(
    (files: SaveFilePayload) =>
      getCustomerApiClient()
        .saveEntityFile(null, files)
        .then(({ data }) => data),
    {
      onSuccess: async (response) => {
        analytics?.addEvents([
          {
            type: userActivityEvents.documentUploaded,
            details: {
              pagePath: window.location.pathname,
              entityId,
              entitySchema: entityType
            },
            timestamp: new Date().toISOString()
          }
        ])
        toast.success({
          title: t('my_orders.fileupload.success_title'),
          description: t('my_orders.fileupload.success_message')
        })

        // Update the list without invalidating the cache
        const currentQueryData = queryClient.getQueryData([
          CUSTOMER_QUERY_IDS.GET_DOCUMENTS,
          entityId,
          PAGE_SIZE
        ]) as { pages: { results: []; hits: number }[] }

        const firstPage = currentQueryData?.pages[0]

        const newQueryData = {
          pageParams: [undefined],
          pages: [
            {
              results: [
                ...response.createdFiles,
                ...firstPage.results.slice(
                  0,
                  PAGE_SIZE - response.createdFiles.length
                )
              ],
              hits: firstPage.hits + 1
            }
          ]
        }

        queryClient.setQueryData(
          [CUSTOMER_QUERY_IDS.GET_DOCUMENTS, entityId, PAGE_SIZE],
          newQueryData
        )

        setIsLoading(false)
      },
      onError: (err) => handleUploadFileError(err as string)
    }
  )

  const deleteFileMutation = useMutation(
    (file: DeleteFilePayload) =>
      getCustomerApiClient()
        .deleteEntityFile(null, file)
        .then(({ data }) => data),
    {
      onSuccess: async (response) => {
        analytics?.addEvents([
          {
            type: userActivityEvents.documentDeleted,
            details: {
              pagePath: window.location.pathname,
              entityId,
              entitySchema: entityType,
              documentId: response.deletedFiles[0]
            },
            timestamp: new Date().toISOString()
          }
        ])
        toast.success({
          title: t('my_orders.fileupload.success_title'),
          description: t('my_orders.fileupload.delete_file')
        })

        const deletedFiles = new Set(response.deletedFiles || [])

        // Update the list without invalidating the cache
        const currentQueryData = queryClient.getQueryData([
          CUSTOMER_QUERY_IDS.GET_DOCUMENTS,
          entityId,
          PAGE_SIZE
        ]) as { pageParams: []; pages: { results: File[]; hits: number }[] }

        const newQueryData = {
          pageParams: currentQueryData.pageParams,
          pages: currentQueryData.pages.map((page) => ({
            ...page,
            results: page.results?.filter((item) => !deletedFiles.has(item._id))
          }))
        }

        queryClient.setQueryData(
          [CUSTOMER_QUERY_IDS.GET_DOCUMENTS, entityId, PAGE_SIZE],
          newQueryData
        )

        setIsDeletingId(undefined)
      },
      onError: (errorMsg) => {
        setIsDeletingId(undefined)
        console.error(errorMsg)

        toast.error({
          title: t('my_orders.fileupload.delete_file_error_title'),
          description: `${t('my_orders.fileupload.delete_file_error_message', {
            message: errorMsg
          })}`
        })
      }
    }
  )

  const openUploader = (): void => {
    fileInputRef.current.click()
  }

  const handleUploadFileError = (errorMsg: string): void => {
    setIsLoading(false)
    console.error(errorMsg)

    toast.error({
      title: t('my_orders.fileupload.error_title'),
      description: `${t('my_orders.fileupload.error_message', {
        message: errorMsg
      })}`
    })
  }

  const handleDownloadAll = async (files: EntityItem[]) => {
    if (isDownloadingAll) return

    try {
      setIsDownloadingAll(true)
      await downloadAsZip(files as FileItem[])
      analytics?.addEvents([
        {
          type: userActivityEvents.allDocumentsDownloaded,
          details: {
            pagePath: window.location.pathname,
            entityId,
            entitySchema: entityType,
            documentIds: files?.map((file) => file?._id)
          },
          timestamp: new Date().toISOString()
        }
      ])
    } catch (error) {
      toast.error({
        title: t('common.error.something_went_wrong'),
        description: t('common.error.download_failed', { error: error.message })
      })
      datadogRum?.addError(error, { action: 'download' })
    } finally {
      setIsDownloadingAll(false)
    }
  }

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files)
    const payload: FilePayload[] = []

    const isExtensionAllowed = getIsFileExtensionAllowed(
      portalConfigData.portalConfig.allowed_file_extensions || {}
    )

    if (!files.length) return

    for (const file of files) {
      const { name, type, size } = file

      // TODO: This check has no equivalent on the API!
      // Check if file is larger than 20MB
      if (size > MAX_FILE_SIZE * 1024 * 1024) {
        handleUploadFileError(
          t('File size exceeds {{size}}MB', { size: MAX_FILE_SIZE })
        )

        return
      }

      if (!isExtensionAllowed(name)) {
        handleUploadFileError(t('File type not supported'))

        return
      }

      try {
        setIsLoading(true)
        const { data: uploadedFile } = await FilesClient.uploadFilePublic(
          null,
          {
            filename: name,
            mime_type: type
          }
        )

        await axios.put(uploadedFile.upload_url, file, {
          headers: { 'Content-Type': type }
        })

        payload.push({
          filename: name,
          s3ref: uploadedFile.s3ref
        })
      } catch (err) {
        handleUploadFileError(err.message)
      }
    }

    await saveFileMutation.mutateAsync({
      files: payload,
      entity_id: entityId,
      entity_type: entityType
    })

    // To allow uploading the same file again
    event.target.value = ''
  }

  const deleteFile = async (fileId: string) => {
    setIsDeletingId(fileId)

    await deleteFileMutation.mutateAsync({
      entity_id: entityId,
      entity_type: entityType,
      file_entity_ids: [fileId]
    })
  }

  const allowedExtensionsHint = Object.entries(
    portalConfigData.portalConfig.allowed_file_extensions || {}
  ).map(([kind, extensions]) => (
    <div key={kind}>
      <FileKind>{t(`file_kind.${kind}`)}</FileKind>: {extensions.join(', ')}
    </div>
  ))

  return (
    <Container>
      {!isUploadDocumentDetailsPermitted && !files.length && (
        <AlertMessage message={'documents.no_documents'} />
      )}

      {isUploadDocumentDetailsPermitted && (
        <>
          <Box marginBottom="3px">
            <Subtitle>{t('Add files')}</Subtitle>
          </Box>
          <Button
            color="primary"
            disabled={
              isLoading || isDeleting || isConfigMode() || isPreviewMode()
            }
            fullWidth
            onClick={openUploader}
            size="large"
            startIcon={
              isLoading ? (
                <CircularProgress size={16} style={{ marginRight: 10 }} />
              ) : (
                <Icon fontSize="small" name="upload" translate="no" />
              )
            }
            variant="contained"
          >
            {t('my_orders.upload_new_document')}
          </Button>
        </>
      )}

      {isUploadDocumentDetailsPermitted && (
        <input
          multiple={true}
          onChange={handleFileUpload}
          ref={fileInputRef}
          style={{ display: 'none' }}
          type="file"
        />
      )}

      {isUploadDocumentDetailsPermitted && (
        <Box
          display="flex"
          justifyContent="center"
          marginBottom="24px"
          marginTop="1.5rem"
        >
          <HintsText>
            {t('Maximum file size: {{size}}MB.', { size: MAX_FILE_SIZE })}
          </HintsText>
          <HintsText>
            {t('Allowed file types')}:{' '}
            <Tooltip title={<>{allowedExtensionsHint}</>}>
              <HoverHint>{t('common extensions')}</HoverHint>
            </Tooltip>
            .
          </HintsText>
        </Box>
      )}

      {/* Download all button */}
      <Box alignItems="flex-end" display="flex" marginBottom="4px">
        <Box flex="1">
          <Subtitle>{t('my_orders.documents_and_pictures')}</Subtitle>
        </Box>

        <DownloadAllContainer>
          <Button
            color="primary"
            disabled={!files?.length || !!isDownloadingAll || isDeleting}
            onClick={() => handleDownloadAll(files)}
            startIcon={
              isDownloadingAll ? (
                <CircularProgress size={16} />
              ) : (
                <Download variant="filled" />
              )
            }
          >
            {t('option_buttons.download_all_documents')}
          </Button>
        </DownloadAllContainer>
      </Box>

      {/* Loading state */}
      {status === 'loading' && (
        <>
          <Box display="grid" gridRowGap="4px" marginBottom="24px">
            <GreyStaticSkeleton
              $borderRadius={`${theme.shape.borderRadius}px`}
              $height="62px"
            />
            <GreyStaticSkeleton
              $borderRadius={`${theme.shape.borderRadius}px`}
              $height="62px"
            />
            <GreyStaticSkeleton
              $borderRadius={`${theme.shape.borderRadius}px`}
              $height="62px"
            />
          </Box>
        </>
      )}

      {/* List of files */}
      {!!files.length && (
        <Box display="grid" gridRowGap="4px" marginBottom="24px">
          {files.map((file: EntityItem) => (
            <DocumentContainer
              $disabled={isLoading || isDeleting}
              key={file._id}
            >
              <DownloadLink
                href={file.public_url || '#'}
                onClick={(e) => checkFileLink(e, file._id)}
                rel="noreferrer noopener"
                target="_blank"
              >
                <IconContainer>
                  <DocumentIcon className="size-4" />
                </IconContainer>

                <Box
                  display="flex"
                  flex="1"
                  flexDirection="column"
                  justifyContent="center"
                  overflow="hidden"
                >
                  <DocumentTitle>
                    {removeFileExtension(file.filename)}
                  </DocumentTitle>

                  <DocumentSubtitle>
                    {file.readable_size}
                    {file.readable_size && file.mime_type && <Dot />}
                    {file.mime_type}
                  </DocumentSubtitle>
                </Box>
              </DownloadLink>

              {!isLoading &&
                !isDeleting &&
                !isDownloadingAll &&
                isDeleteDocumentDetailsPermitted && (
                  <ActionsContainer>
                    <ActionContainer
                      onClick={() =>
                        deleteFile(file?.$relation?.entity_id ?? file._id)
                      }
                    >
                      <Icon
                        color="secondary"
                        fontSize="small"
                        name="delete"
                        style={{ fontSize: 18 }}
                        translate="no"
                      />
                    </ActionContainer>
                  </ActionsContainer>
                )}

              {isDeletingId === file._id && (
                <ActionsContainer>
                  <ActionContainer>
                    <CircularProgress size={16} />
                  </ActionContainer>
                </ActionsContainer>
              )}
            </DocumentContainer>
          ))}

          {hasMore && (
            <Box display="flex" justifyContent="center" marginTop={2}>
              <Button disabled={isLoadingMore} onClick={fetchMore}>
                {!isLoadingMore && t('documents.load_more')}
                {isLoadingMore && <CircularProgress size={16} />}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Container>
  )
}

export default RequestDocuments

const Subtitle = styled(BodyText)`
  color: ${({ theme }) => theme.palette.grey[200]};
  font-weight: 700;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 1.5px;
  margin-bottom: 4px;
`

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e6eef7;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-right: 12px;
  background: white;
`

const DocumentContainer = styled.div`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid #e6eef7;
  display: flex;
  overflow: hidden;
  background: #f7fbff;
  padding: 10px 8px;

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
    `};
`

const Container = styled.div``

const DocumentTitle = styled(BodyText)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: 600;
`

const DocumentSubtitle = styled(SmallText)`
  color: ${({ theme }) => theme.palette.grey[500]};
`

const DownloadLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.secondary};
  overflow: hidden;
  display: flex;
  flex: 1;

  @media ${MEDIA_SIZES.greater.tablet} {
    :hover {
      ${DocumentTitle} {
        color: ${({ theme }) => theme.palette.primary[500]};
      }
    }
  }

  :active {
    color: initial;
  }
`

const ActionsContainer = styled.div`
  margin-left: 12px;
`

const ActionContainer = styled.div`
  display: flex;
  padding: 4px;
  cursor: pointer;
  height: fit-content;

  i {
    font-size: 18px !important;
  }
`

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  margin: 0 4px;
  margin-bottom: 2px;
  display: inline-flex;
  background: ${({ theme }) => theme.palette.grey[200]};
`

const DownloadAllContainer = styled.div`
  margin-left: auto;
  padding-left: 8px;

  i {
    font-size: 18px !important;
  }
`

const HintsText = styled.div`
  color: ${({ theme }) => theme.palette.grey[700]};
  font-size: 1.2rem;

  &:not(:last-child) {
    margin-right: 0.5rem;
  }
`

const HoverHint = styled.span`
  text-decoration: underline;
  text-decoration-style: dotted;
`

const FileKind = styled.span`
  font-weight: 600;
`
